import { z } from "zod";

const EMOJI_PREFIX = "emoji:";
const EMOJI_CHARACTER_PATTERN =
  /\p{Extended_Pictographic}|\p{Regional_Indicator}|[#*0-9]\uFE0F?\u20E3/u;

function isSingleEmoji(value: string): boolean {
  if (!value || !EMOJI_CHARACTER_PATTERN.test(value)) return false;

  const segments = new Intl.Segmenter(undefined, {
    granularity: "grapheme",
  }).segment(value);

  return Array.from(segments).length === 1;
}

export function decodeRecipeEmoji(value: string): string | null {
  if (!value.startsWith(EMOJI_PREFIX)) return null;

  try {
    const emoji = decodeURIComponent(value.slice(EMOJI_PREFIX.length));
    return isSingleEmoji(emoji) ? emoji : null;
  } catch {
    return null;
  }
}

export type RecipeVisual =
  | { type: "empty" }
  | { type: "image"; url: string }
  | { type: "emoji"; emoji: string };

export function encodeRecipeEmoji(emoji: string): string {
  if (!isSingleEmoji(emoji)) {
    throw new Error("Recipe visual must contain a single emoji");
  }

  return `${EMOJI_PREFIX}${encodeURIComponent(emoji)}`;
}

export function parseRecipeVisual(
  value: string | null | undefined,
): RecipeVisual {
  if (!value) return { type: "empty" };

  if (value.startsWith(EMOJI_PREFIX)) {
    const emoji = decodeRecipeEmoji(value);
    return emoji ? { type: "emoji", emoji } : { type: "empty" };
  }

  if (z.string().url().safeParse(value).success) {
    return { type: "image", url: value };
  }

  return { type: "empty" };
}

export function getRecipeImageUrl(
  value: string | null | undefined,
): string | null {
  const visual = parseRecipeVisual(value);
  return visual.type === "image" ? visual.url : null;
}
